/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

/**
 * Domain catalogs for inbound response mapping (#866, Universal Mapping Model).
 *
 * Two sides, both autocompleted (the load-bearing inbound win):
 * - **Source** (retrieve): the parsed response fields, per protocol. Paths are
 *   the exact `calculated.path` values (rooted at `response.*`) the resolver
 *   resolves against.
 * - **Target** (store): the `VastAdDto` context fields, optimizer-critical first.
 *
 * See `docs/UNIVERSAL_MAPPING_MODEL.md` and `docs/MAPPING_EDITOR_UX_GUIDANCE.md`.
 */

import { expressionStructureIssues } from './expression-structure';
import { expressionDefaultOf } from './mapping-value-round-trip';
import { runtimeCoercion } from '@expression-yield';
import {
  AD_TARGET_FIELDS,
  adTargetField,
  appliesTo,
  canonicalizeInboundTarget,
  checkDestinationValue,
  encodingAfterCoercion,
  targetNotOfferedReason,
  INBOUND_TARGET_ROOTS,
  USED_BY_OPTIMIZER,
  USED_BY_OPTIMIZER_DETAIL,
  targetLifetime,
  usedByOptimizer,
} from '@destination-field';
import type { DestinationField } from '@destination-field';
import {
  CONTEXT_ROOT_ALIASES,
  CONTEXT_ROOTS,
  describeFanOut,
  describeLifetime,
  describeLifetimeScope,
  describeSharedAcrossFanOut,
  fanOutAt,
  sharedAcrossFanOut,
} from '@context-roots';
import { getSchemaSuggestionsForSurface, markSuggestion } from '../inputs/data-model-schema';
import type { MappingSuggestion } from '../inputs/MappingAutocompleteInput';
import type { MappingDocument } from '../../../types/mapping';
import type { MappingFieldDescriptor, MappingScalarType } from '../../../types/mapping';
import type { MacroExpression } from '../../../types/decorator-expression';
import type { MappingIssue } from './mapping-issues';
import { sharedAcrossFanOutOn } from './shared-expression-usability';

export type ResponseSourceDomain = 'ortb-bid-response' | 'vast-ad-response';

/**
 * **Which resolve context this surface's values are resolved against** (`X18`, and `X32` Q1).
 *
 * Named once here rather than spelled at each call site: `X56a` made it the third question asked of
 * the same key, and three copies of a string is how two of them stop agreeing.
 */
export const RESPONSE_SURFACE = 'responseMapping' as const;

/** ORTB bid response fields (origin = the raw OpenRTB `bid`, exposed as `response`). */
export const ORTB_BID_RESPONSE_CATALOG: MappingFieldDescriptor[] = [
  { path: 'response.price', type: 'number', importance: 'required', description: 'Bid price / CPM' },
  { path: 'response.crid', type: 'string', importance: 'recommended', description: 'Creative ID' },
  { path: 'response.adomain.0', type: 'string', importance: 'recommended', description: 'Advertiser domain (first)' },
  { path: 'response.cat.0', type: 'string', importance: 'recommended', description: 'IAB category (first)' },
  { path: 'response.adm', type: 'string', importance: 'optional', description: 'Ad markup (VAST XML / URL)' },
  { path: 'response.nurl', type: 'string', importance: 'optional', description: 'Win-notice URL' },
  { path: 'response.burl', type: 'string', importance: 'optional', description: 'Billing-notice URL' },
  { path: 'response.dealid', type: 'string', importance: 'optional', description: 'PMP deal ID' },
  { path: 'response.w', type: 'number', importance: 'optional', description: 'Creative width' },
  { path: 'response.h', type: 'number', importance: 'optional', description: 'Creative height' },
  {
    path: 'response.dur',
    type: 'number',
    importance: 'optional',
    description: 'Ad duration (s) — OpenRTB 2.6+ bid field; absent on 2.5',
    when: { protocolVersion: { min: '2.6' } },
  },
  {
    /**
     * **Added with `FD10`, because the side table was gating it and the catalog never declared it.**
     * `RESPONSE_SOURCE_SINCE` carried `response.mtype: '2.6'` against no entry, so the gate applied
     * to a hand-typed path and to nothing the picker offered. Moving the claim onto the entry is
     * what made the absence visible — which is the whole argument for not keeping a claim about a
     * thing in a list beside it (`#1190`).
     */
    path: 'response.mtype',
    type: 'number',
    importance: 'optional',
    description: 'Creative media type (1=banner, 2=video, 3=audio, 4=native) — OpenRTB 2.6+',
    when: { protocolVersion: { min: '2.6' } },
  },
  {
    path: 'response.ext',
    type: 'object',
    importance: 'optional',
    description: 'Bid extension object (e.g. response.ext.encp)',
  },

  // The bid's VAST markup, parsed. Most video DSPs deliver the creative as VAST
  // in `adm` and never set the 2.6 `bid.dur`, so duration, media file and click
  // URL live here and nowhere else on the bid. Before `admParsed` these were
  // reachable only by regex against the whole XML document, while a VAST source
  // mapping the identical markup addressed them by path — a parity gap.
  {
    path: 'response.admParsed.InLine.Creatives.Creative.0.Linear.Duration',
    type: 'string',
    importance: 'recommended',
    description: 'Creative duration from the VAST in adm (hh:mm:ss) — set when the bid carries VAST markup',
  },
  {
    path: 'response.admParsed.InLine.Creatives.Creative.0.Linear.MediaFiles.MediaFile.0.#text',
    type: 'string',
    importance: 'optional',
    description: 'First media file URL from the VAST in adm',
  },
  {
    path: 'response.admParsed.InLine.Creatives.Creative.0.Linear.VideoClicks.ClickThrough',
    type: 'string',
    importance: 'optional',
    description: 'Click-through URL from the VAST in adm',
  },
  {
    path: 'response.admParsed',
    type: 'object',
    importance: 'optional',
    description: 'The bid’s VAST markup, parsed — same shape a VAST source reads as `response`',
  },
];

/**
 * **A source path's minimum protocol version, where it declares one** (`FD10`, `4a`).
 *
 * This was `RESPONSE_SOURCE_SINCE`, a `Record<domain, Record<path, version>>` beside the catalogs,
 * read through here and compared by a private `versionMeets` that used `parseFloat` — so `2.10`
 * read as earlier than `2.9`, the exact error `compareVersions` was written against. Latent rather
 * than live: no protocol gated here has reached a `.10`.
 *
 * The claim now lives on the entry as `when`, the identical declaration `#1315` put on the ORTB
 * destination rows, read by the identical evaluator (`appliesTo`). This helper survives because one
 * caller needs the **number** for its sentence — *"which is OpenRTB 2.6+"* — and not the verdict.
 */
export function responseSourceSinceVersion(domain: ResponseSourceDomain, path: string): string | undefined {
  return getSourceCatalog(domain).find((f) => f.path === path)?.when?.protocolVersion?.min;
}

/**
 * VAST ad response fields. The driver parses the ad XML into JSON and exposes it
 * as `response` (like ORTB), so extraction is JSON-path based. Attribute-less
 * elements are bare-string paths (`response.InLine.Advertiser`); attributed ones
 * carry `.#text` (`response.InLine.Pricing.#text` + `…Pricing.@_model`);
 * repeatable elements are arrays (index with `.0`). `response.raw` (raw ad XML)
 * remains for regex fallback / custom elements not surfaced here. Custom
 * Extension fields are reachable at `response.InLine.Extensions.Extension.0.*`.
 */
export const VAST_AD_RESPONSE_CATALOG: MappingFieldDescriptor[] = [
  {
    path: 'response.InLine.Pricing.#text',
    type: 'number',
    importance: 'required',
    description: 'Bid / CPM (<Pricing> value)',
    when: { protocolVersion: { min: '3.0' } },
  },
  {
    path: 'response.InLine.Advertiser',
    type: 'string',
    importance: 'recommended',
    description: 'Advertiser domain (<Advertiser>)',
    when: { protocolVersion: { min: '3.0' } },
  },
  {
    path: 'response.InLine.Category',
    type: 'string',
    importance: 'recommended',
    description: 'IAB category (<Category>)',
    // VAST **4.0**, not 3.0 (`#1319` item 5): `vast3_draft.xsd` declares no `Category`, so a 3.0
    // source was not told it would not send one. It carries a required `authority` attribute, and
    // the path walker reads the element's text either way (`#1402`).
    when: { protocolVersion: { min: '4.0' } },
  },
  { path: 'response.InLine.AdSystem', type: 'string', importance: 'optional', description: 'Ad system (<AdSystem>)' },
  { path: 'response.InLine.AdTitle', type: 'string', importance: 'optional', description: 'Ad title (<AdTitle>)' },
  {
    path: 'response.InLine.Impression.0',
    type: 'string',
    importance: 'optional',
    description: 'First <Impression> URL',
  },
  {
    path: 'response.InLine.Creatives.Creative.0.Linear.Duration',
    type: 'string',
    importance: 'optional',
    description: 'Duration (HH:MM:SS)',
  },
  {
    path: 'response.InLine.Creatives.Creative.0.Linear.MediaFiles.MediaFile.0.#text',
    type: 'string',
    importance: 'optional',
    description: 'First <MediaFile> URL',
  },
  {
    path: 'response.InLine.Creatives.Creative.0.Linear.VideoClicks.ClickThrough',
    type: 'string',
    importance: 'optional',
    description: '<ClickThrough> URL',
  },
  // `#1319` item 4: the sources whose ad target already exists. `Tracking` is not offered: the parse
  // gives a list of `{ event, url }` elements and `ad.tracking` is a map keyed by event, which the
  // driver builds itself, so no single path can fill it. `Ad@id` is outside `response`, which starts
  // inside `<Ad>`, and the driver reads it.
  {
    path: 'response.InLine.Error.0',
    type: 'string',
    importance: 'optional',
    description: 'First <Error> URL',
  },
  {
    path: 'response.InLine.Creatives.Creative.0.Linear.@_skipoffset',
    type: 'string',
    importance: 'optional',
    description: 'Skip offset (<Linear skipoffset>)',
  },
  {
    path: 'response.InLine.Creatives.Creative.0.Linear.MediaFiles.MediaFile.0.@_type',
    type: 'string',
    importance: 'optional',
    description: 'First <MediaFile> MIME type',
  },
  {
    path: 'response.InLine.Creatives.Creative.0.Linear.MediaFiles.MediaFile.0.@_width',
    type: 'number',
    importance: 'optional',
    description: 'First <MediaFile> width',
  },
  {
    path: 'response.InLine.Creatives.Creative.0.Linear.MediaFiles.MediaFile.0.@_height',
    type: 'number',
    importance: 'optional',
    description: 'First <MediaFile> height',
  },
  // A wrapper ad has no `InLine`: everything above is absent on one, and its own elements live here.
  {
    path: 'response.Wrapper.VASTAdTagURI',
    type: 'string',
    importance: 'optional',
    description: 'Wrapper: the tag it points to (<VASTAdTagURI>)',
  },
  { path: 'response.Wrapper.AdSystem', type: 'string', importance: 'optional', description: 'Wrapper: <AdSystem>' },
  {
    path: 'response.Wrapper.Impression.0',
    type: 'string',
    importance: 'optional',
    description: 'Wrapper: first <Impression> URL',
  },
  {
    path: 'response.Wrapper.Error.0',
    type: 'string',
    importance: 'optional',
    description: 'Wrapper: first <Error> URL',
  },
  {
    path: 'response.InLine.Extensions.Extension.0',
    type: 'object',
    importance: 'optional',
    description: 'First <Extension> — drill in for custom fields',
  },
  {
    path: 'response.raw',
    type: 'string',
    importance: 'optional',
    description: 'Raw ad XML — pair with a regex (Expression tab) for anything above not surfaced',
  },
];

/**
 * **The ad's fields, offered or declared out** — declared in `destination-field.ts` as
 * `DestinationField`s (`FD1`, `4b`), because the resolver refuses what this declines and the two were
 * two lists. Re-exported so the editors keep one import.
 */
export { AD_TARGET_FIELDS, adTargetField, targetNotOfferedReason };

/** The fields the picker offers — everything not declared out. */
export const AD_TARGET_CATALOG: readonly DestinationField[] = AD_TARGET_FIELDS.filter((f) => !f.notOffered);

/** The stored spelling of a target typed or held in the pre-`X49` form. */
export function canonicalTarget(path: string): string {
  return canonicalizeInboundTarget(path.split('.').filter(Boolean)).join('.');
}

/** The namespaces an inbound mapping may write to — declared once, in `destination-field.ts`. */
export const TARGET_ROOTS = INBOUND_TARGET_ROOTS;

/** Optimizer-critical target paths — the "add-recommended" / "won't populate" set. */
export const OPTIMIZER_CRITICAL_TARGETS: ReadonlyArray<string> = [
  'ad.bid',
  'ad.durationSeconds',
  'ad.adDomain',
  'ad.iabCategory',
  'ad.adId',
];

/** The source catalog for a protocol. */
export function getSourceCatalog(domain: ResponseSourceDomain): MappingFieldDescriptor[] {
  return domain === 'vast-ad-response' ? VAST_AD_RESPONSE_CATALOG : ORTB_BID_RESPONSE_CATALOG;
}

/**
 * ORTB standard bid path per target (mirrors the driver default ORTB document).
 * `durationSeconds` is deliberately absent: the driver extracts duration
 * **structurally from the VAST inside `adm`** (`parseVastFromAdm`, 30s fallback),
 * NOT from `bid.dur` — which is 2.6-only and absent on 2.5. Recommending
 * `response.dur` here would seed a mapping the driver doesn't use and that
 * silently won't populate on a 2.5 exchange. `response.dur` stays available as a
 * (2.6-gated) suggestion for operators who genuinely want the bid-level override.
 */
const ORTB_STANDARD_SOURCE: Record<string, { path: string; asType: MappingScalarType }> = {
  /**
   * **Where the driver reads what it fills structurally, as a path** (`#1250`). *Edit* on an
   * *Extracted automatically* row opened a blank path for these, so taking one over meant rebuilding
   * the driver's read from memory. Each path was read off the real driver's `response` root.
   *
   * They cannot disagree with the driver in a way that loses a value: when a path finds nothing (a
   * companion creative ahead of the linear one puts something else at `Creative.0`), the entry
   * produces nothing and the driver's own value stands. `ad.seat` has no path — the seat is on the
   * `seatbid` around the bid, and `response` is the bid.
   */
  'ad.width': { path: 'response.w', asType: 'number' },
  'ad.height': { path: 'response.h', asType: 'number' },
  'ad.dealId': { path: 'response.dealid', asType: 'string' },
  'ad.durationSeconds': { path: 'response.admParsed.InLine.Creatives.Creative.0.Linear.Duration', asType: 'number' },
  'ad.duration': { path: 'response.admParsed.InLine.Creatives.Creative.0.Linear.Duration', asType: 'string' },
  'ad.mediaUrl': {
    path: 'response.admParsed.InLine.Creatives.Creative.0.Linear.MediaFiles.MediaFile.0.#text',
    asType: 'string',
  },
  'ad.bid': { path: 'response.price', asType: 'number' },
  'ad.adId': { path: 'response.crid', asType: 'string' },
  'ad.adDomain': { path: 'response.adomain.0', asType: 'string' },
  'ad.iabCategory': { path: 'response.cat.0', asType: 'string' },
  'ad.winNoticeUrl': { path: 'response.nurl', asType: 'string' },
  'ad.billingNoticeUrl': { path: 'response.burl', asType: 'string' },
};

/**
 * Targets the **driver already fills structurally**, and how — the inbound
 * mirror of `ORTB_DRIVER_MERGE_BASE` (guidance §15: state what the runtime
 * supplies for free).
 *
 * Without this the surface is silent about them, and silence reads as "nothing
 * is handling this". A tester watching `<Duration>00:00:30</Duration>` come back
 * in an ORTB `adm` reasonably concluded duration was unmapped and went hunting
 * for a path — when the driver had already parsed it. An unstated no-op costs
 * more than a stated one.
 *
 * Rendered as **one stated no-op on the surface**, not as rows in the warnings
 * list. Seven `info` entries on every source would set `worstLevel` to `info`
 * permanently, put a dot on the phase for something needing no action, and bury
 * the warnings that do — the same failure a provenance system dies of when it
 * chips every value.
 */
/**
 * What the driver already produces without any authored mapping — **both** mechanisms.
 *
 * There are two, and the console only knew about the first, which is what made
 * "Already extracted for you" understate the truth while "+ Add recommended" offered
 * precisely the fields it had failed to mention (#1013):
 *
 * 1. **Parsed structurally by driver code** — duration out of `<Duration>`, the media
 *    file out of `<MediaFile>`, `bid.w`/`bid.h`.
 * 2. **The driver's default mapping document** — `defaultVastResponseMapping()` /
 *    `defaultOrtbResponseMapping()` in `libs/drivers/default-response-mappings.ts`,
 *    applied under any authored entries.
 *
 * `response-field-catalog.driver-defaults.test.ts` parses that backend file and fails if
 * a target it declares is missing here, so the mirror cannot silently fall behind again.
 *
 * **This is deliberately a mirror, and the exit is recorded** (M3): the right answer is
 * for the console to *fetch* the default document the way it fetches system defaults and
 * the device catalog, at which point `recommendedSourceFor` disappears rather than being
 * corrected. Mirror-plus-spec is the MVP because the surface is S1 and drift, not
 * absence, was the actual defect. See #1013.
 */
export interface DriverSuppliedTarget {
  /**
   * **The short name of what the driver read**, for the *accept the system default* control
   * (`X33a`, `#1267`). `describeSystemDefault` carries the same slot on a parameter row and returns
   * a concept rather than a sentence, so this matches it: an operator declining a fall-back needs to
   * know *which* value they are turning off, and a paragraph beside a checkbox is not that.
   */
  from: string;
  /** The full sentence, for the *already extracted for you* panel, where there is room for it. */
  detail: string;
}

/**
 * **One entry answering both questions, rather than a second map keyed the same way.** The panel
 * wants a sentence and the checkbox wants a name; kept apart they would be a hand-maintained
 * parallel list of the same set, which is the shape this repository keeps paying for.
 */
export const DRIVER_SUPPLIED_TARGETS: Record<ResponseSourceDomain, Record<string, DriverSuppliedTarget>> = {
  'ortb-bid-response': {
    'ad.durationSeconds': {
      from: '<Duration> in adm',
      detail:
        'parsed from the `<Duration>` in the VAST inside `adm`, falling back to 30s. Map `response.admParsed…Duration` only to override it.',
    },
    'ad.duration': {
      from: '<Duration> in adm',
      detail: 'parsed from the `<Duration>` in the VAST inside `adm`, falling back to 00:00:30.',
    },
    'ad.mediaUrl': {
      from: '<MediaFile> in adm',
      detail: 'taken from the VAST `<MediaFile>` in `adm`, or from `adm` itself when it is a bare URL.',
    },
    'ad.width': { from: 'bid.w', detail: 'taken from `bid.w`.' },
    'ad.height': { from: 'bid.h', detail: 'taken from `bid.h`.' },
    'ad.dealId': { from: 'bid.dealid', detail: 'taken from `bid.dealid`.' },
    'ad.seat': {
      from: 'seatbid.seat',
      detail:
        'taken from the parent `seatbid.seat`. No mapping path reaches it — a mapping reads the bid itself — so an override starts blank.',
    },
    // ── from the driver's default mapping document ──
    'ad.bid': { from: 'bid.price', detail: 'taken from `bid.price`.' },
    'ad.adId': { from: 'bid.crid', detail: 'taken from `bid.crid`.' },
    'ad.adDomain': { from: 'bid.adomain[0]', detail: 'taken from the first entry of `bid.adomain`.' },
    'ad.iabCategory': { from: 'bid.cat[0]', detail: 'taken from the first entry of `bid.cat`.' },
    'ad.winNoticeUrl': { from: 'bid.nurl', detail: 'taken from `bid.nurl`.' },
    'ad.billingNoticeUrl': { from: 'bid.burl', detail: 'taken from `bid.burl`.' },
  },
  'vast-ad-response': {
    'ad.durationSeconds': {
      from: '<Duration>',
      detail: 'parsed from `<Duration>` and converted to seconds, falling back to 30s.',
    },
    'ad.duration': { from: '<Duration>', detail: 'parsed from `<Duration>`.' },
    'ad.adId': {
      from: '<Ad id>',
      detail:
        'taken from the `<Ad id="…">` attribute. No mapping path reaches it — a mapping reads inside `<Ad>` — so an override starts blank.',
    },
    'ad.mediaUrl': { from: '<MediaFile>', detail: 'taken from the first `<MediaFile>`.' },
    // ── from the driver's default mapping document ──
    'ad.bid': { from: '<Pricing>', detail: 'taken from `<Pricing>`.' },
    'ad.adDomain': { from: '<Advertiser>', detail: 'taken from `<Advertiser>`.' },
    'ad.iabCategory': { from: '<Category>', detail: 'taken from `<Category>`.' },
  },
};

/**
 * The standard source expression for a target — used to seed "add recommended"
 * with a WORKING mapping (not a bare `response.raw`). Mirrors the backend driver
 * default documents: ORTB paths into the bid; VAST regexes over the raw XML for
 * bid/advertiser/category, and paths into the parsed ad for duration/adId.
 */
export function recommendedSourceFor(
  domain: ResponseSourceDomain,
  target: string,
): { source: MacroExpression; asType: MappingScalarType } | null {
  // Callers pass whatever the row holds, which may be the pre-`X49` spelling.
  const canonical = canonicalTarget(target);
  if (domain === 'ortb-bid-response') {
    const std = ORTB_STANDARD_SOURCE[canonical];
    return std ? { source: { type: 'calculated', path: std.path }, asType: std.asType } : null;
  }
  // VAST — JSON paths over the parsed ad. Duration and the media file are the driver's structural
  // reads written as paths (`#1250`); `ad.durationSeconds` converts `hh:mm:ss` on the way in (`#933`).
  // `ad.adId` has none: the driver reads the `<Ad id>` attribute, and `response` starts inside `<Ad>`.
  switch (canonical) {
    case 'ad.durationSeconds':
      return {
        source: { type: 'calculated', path: 'response.InLine.Creatives.Creative.0.Linear.Duration' },
        asType: 'number',
      };
    case 'ad.duration':
      return {
        source: { type: 'calculated', path: 'response.InLine.Creatives.Creative.0.Linear.Duration' },
        asType: 'string',
      };
    case 'ad.mediaUrl':
      return {
        source: {
          type: 'calculated',
          path: 'response.InLine.Creatives.Creative.0.Linear.MediaFiles.MediaFile.0.#text',
        },
        asType: 'string',
      };
    case 'ad.bid':
      return { source: { type: 'calculated', path: 'response.InLine.Pricing.#text' }, asType: 'number' };
    case 'ad.adDomain':
      return { source: { type: 'calculated', path: 'response.InLine.Advertiser' }, asType: 'string' };
    case 'ad.iabCategory':
      return { source: { type: 'calculated', path: 'response.InLine.Category' }, asType: 'string' };
    default:
      return null;
  }
}

function toSuggestions(fields: MappingFieldDescriptor[], query: string): MappingSuggestion[] {
  const q = query.trim().toLowerCase();
  return fields
    .filter((f) => !q || f.path.toLowerCase().includes(q))
    .map((f) => ({ value: f.path, label: f.path, category: f.importance, description: f.description }));
}

/**
 * Autocomplete for the retrieve (source) side of an inbound mapping. Version-aware:
 * a source path gated above the source's protocol version (e.g. ORTB `response.dur`
 * on a 2.5 exchange, VAST `<Pricing>` on a 2.0 tag) is hidden, since reading it
 * would never populate. An absent version shows everything (advisory).
 */
export function sourceSuggestions(domain: ResponseSourceDomain, query: string, version?: string): MappingSuggestion[] {
  // The one evaluator (`FD10`): `appliesTo` compares part by part and treats an unstated version
  // as *no opinion*, which is what keeps the catalog advisory rather than hiding fields when the
  // source has not declared a version.
  const fields = getSourceCatalog(domain).filter((f) => appliesTo(f, { protocolVersion: version }));
  return [...toSuggestions(fields, query), ...objectModelSuggestions(query)].map(withCardinalityMark);
}

/**
 * **This surface runs once for every ad in the response** — `X56a` (`#1296`), the surface's half.
 *
 * The builder key is the one the surface already names for `X32` question 1, so the fan-out and
 * *what resolves here* come from the same declaration rather than from a second list.
 */
export const RESPONSE_FAN_OUT = fanOutAt(RESPONSE_SURFACE) ?? null;

/**
 * **What this surface is, in one sentence** — and the fan-out is the first clause of it.
 *
 * It was a second line beneath the intro copy for an afternoon, which Todd collapsed on reading it
 * (2026-09-10): *"I think we can collapse the intro into a shorter description that covers both
 * things."* Right, and it is this rule set's own — two sentences making one claim about the same
 * surface is a duplicate whichever of them is read first, and the fan-out is not an aside about the
 * surface, it is **what the surface does**.
 *
 * Only the *each ad in the response* fragment comes from the declaration; the rest is this surface's
 * copy, because what the driver already extracts is true here and nowhere else.
 */
export const RESPONSE_INTRO = RESPONSE_FAN_OUT
  ? `Maps values from ${describeFanOut(RESPONSE_FAN_OUT)} into a field. Optimizer-critical fields (bid, duration, advertiser domain, IAB category, ad ID) are extracted by default. Override here for any custom or non-standard response formats.`
  : null;

/**
 * **The mark on a source path that does not vary across the ads** — the exception to the sentence
 * above, and the only marked case (`#1259`: the mark corrects an assumption, and the surface has
 * just created the assumption that everything here is per ad).
 *
 * It carries **no severity**. Reading `demandSource.floorCpm` beside an extracted price is exactly
 * what that root is offered for; a warning beside correct behaviour trains an operator to ignore
 * the warnings that matter.
 */
export function sourceCardinalityMark(path: string): { label: string; detail: string } | null {
  if (!RESPONSE_FAN_OUT) return null;
  if (sharedAcrossFanOut(RESPONSE_SURFACE, path) !== true) return null;
  const root = path.split('.')[0];
  return {
    label: describeSharedAcrossFanOut(RESPONSE_FAN_OUT),
    detail: `\`${root}.*\` is built once for the whole response, so this reads the same value on every ${RESPONSE_FAN_OUT.per}.`,
  };
}

/**
 * **The same mark, for a whole row** — a row's source is an expression, not a path.
 *
 * `sharedAcrossFanOutOn` answers over every path the expression reads and declines to answer where
 * it cannot see them all, so a conditional comparing `demandSource.floorCpm` against
 * `response.price` is correctly **unmarked**: one of its inputs varies, so the row does.
 */
export function rowCardinalityMark(expression: MacroExpression | undefined): { label: string; detail: string } | null {
  if (!RESPONSE_FAN_OUT) return null;
  if (sharedAcrossFanOutOn(expression, RESPONSE_SURFACE) !== true) return null;
  return {
    label: describeSharedAcrossFanOut(RESPONSE_FAN_OUT),
    detail: `Everything this row reads is built once for the whole response, so it resolves to the same value on every ${RESPONSE_FAN_OUT.per}.`,
  };
}

/** The type-down's half of the same mark — text, for the reason {@link withGrainMark} gives. */
function withCardinalityMark(suggestion: MappingSuggestion): MappingSuggestion {
  return markSuggestion(suggestion, [sourceCardinalityMark(suggestion.value)?.label]);
}

/**
 * **The object model beside the response itself** (`#1236`, `X47`, marked rather than filtered by
 * `#1248`).
 *
 * Measured 2026-09-04: `applyResponseMapping` sets `pod`, `demandSource`, `session` and `response`,
 * so all four resolve here today — and the console offered only the last, which is what made the
 * other three invisible. An operator was never shown that `demandSource.floorCpm` is reachable on
 * the surface where comparing an extracted bid to the floor is the obvious thing to want.
 *
 * **The surface is named, not its roots.** `getSchemaSuggestionsForSurface` reads
 * `RESOLVE_CONTEXT_BUILDERS` itself — which `context-roots.spec.ts` checks against the real
 * construction site — so a root added or removed there moves this type-down with it, and
 * `params`/`headers` arrive **marked** rather than missing (`X32`: hiding answers by silence).
 *
 * `response` is dropped **here and only here**, because the per-protocol catalogues above already
 * carry it with types, descriptions and version gating the generic model has none of. Two entries for
 * one path is `X3` inside a type-down — and this is a de-duplication, not a claim about the phase,
 * which is why it is a filter and the grain question is not.
 */
function objectModelSuggestions(query: string): MappingSuggestion[] {
  return getSchemaSuggestionsForSurface(query, RESPONSE_SURFACE).filter(
    (s) => s.value !== 'response' && !s.value.startsWith('response.'),
  );
}

/**
 * Every first segment a mapping **reads** from — the declared context roots and their aliases, from
 * the one declaration. A response target starting with one of these names a place to write that does
 * not exist yet (`#1297`).
 */
const CONTEXT_ROOT_SPELLINGS: ReadonlySet<string> = new Set([
  ...CONTEXT_ROOTS.map((r) => r.name),
  ...CONTEXT_ROOT_ALIASES.map((a) => a.spelling),
]);

/**
 * Validate an inbound response-mapping document (guidance §16). Nothing here blocks Save —
 * reading a response never causes a hard reject, and an error is advisory (`R3a`):
 *  - **a target naming a context root** (error) — it is written onto the ad, where nothing reads it.
 *  - **won't populate** (warn) — an entry with a target but an empty source path.
 *  - **version-inappropriate source** (warn) — an authored source path that only exists
 *    from a later protocol version than this source speaks (ORTB 2.6 `response.dur`
 *    on a 2.5 exchange; VAST 3.0 `<Pricing>` on a 2.0 tag).
 *  - **declining the driver's value on a field the optimizer selects on** (warn).
 */
export function validateResponseMapping(doc: MappingDocument | undefined | null, version?: string): MappingIssue[] {
  const entries = doc?.entries ?? [];
  const domain = (doc?.sourceDomain ?? 'ortb-bid-response') as ResponseSourceDomain;
  const issues: MappingIssue[] = [];

  for (const entry of entries) {
    const target = entry.target.join('.') || '(unnamed)';
    const src = entry.source;

    // What is unfinished **inside** the row's expression (`4c`, `#1334`). First, and outside the
    // checks below that end the row early — an unwritable target does not make its expression's
    // structure any less the operator's to finish.
    issues.push(...expressionStructureIssues(src, `\`${target}\``, target, 'other-vocabulary'));

    /**
     * **A target naming a context root is written onto the ad** (`#1297`).
     *
     * A first segment that is not a declared target root is read as the pre-`X49` ad-relative
     * spelling, so `session.custom.rating` is stored as `ad.session.custom.rating`: persisted,
     * round-tripped, and read by nothing. That is right for a custom field on the ad and wrong for a
     * segment naming something a mapping reads from, which is an author asking for a destination
     * that does not exist yet (`#1235`).
     *
     * **An error, never a block** — an authored value that cannot work, not a missing one.
     *
     * Detected by comparing with the canonical spelling rather than by listing roots a second time:
     * once `pod` is a declared target root, `pod.total` canonicalises to itself and stops being
     * reported, with nothing here to change.
     */
    const written = canonicalizeInboundTarget(entry.target).map(String);
    const first = entry.target[0];
    if (typeof first === 'string' && written[0] !== first && CONTEXT_ROOT_SPELLINGS.has(first)) {
      issues.push({
        kind: 'ignored',
        message: `\`${target}\` names \`${first}\`, which a mapping can read from but a response mapping cannot write to yet. This row stores the value on the ad as \`${written.join('.')}\`, where nothing reads it — pick a field on the ad instead.`,
        ref: target,
      });
    }

    /**
     * **A withheld target is ignored when the response is read** (`#1320`, slice `2a`). The picker
     * does not offer these, so a row reaches here only from storage or by typing, and the resolver
     * refuses it. An error, never a block.
     */
    const withheld = targetNotOfferedReason(written.join('.'));
    if (withheld) {
      issues.push({
        kind: 'ignored',
        message: `\`${target}\` is not written from a response: ${withheld}. This row is ignored.`,
        ref: target,
      });
      continue;
    }

    /**
     * **A literal written to a declared field is graded by what the field accepts** (`FD4`, `4b`).
     *
     * The target is a `DestinationField` since `FD1`, and a `static` source and a row's default are
     * both literals an operator typed for it — the default most of all, since it is what lands when
     * the source resolves to nothing, *the case it existed for*, when nobody is watching (`#914`).
     * The ad is a typed object, so a literal is read as `json` unless the resolver coerces it — the
     * row's `Type`, else the target's own (`FD13`, `encodingAfterCoercion`). A shared expression's own default is
     * its author's and is graded there; only this row's override is this row's.
     */
    const field = adTargetField(written.join('.'));
    if (field) {
      const encoding = encodingAfterCoercion('json', runtimeCoercion(entry.asType, src, field.valueType ?? null));
      for (const literal of [src.type === 'static' ? src.value : undefined, expressionDefaultOf(src, null)]) {
        const finding = checkDestinationValue(field, literal, encoding);
        if (finding) issues.push({ kind: finding.kind, message: finding.message, ref: target });
      }
    }

    // Won't populate: a calculated source with no path (a blank override does nothing).
    //
    // A regex does NOT excuse a missing path, and used to. `_resolveCalculated`
    // resolves the path FIRST and returns early when it is undefined, so the
    // regex never runs — a regex with no path to match against is dead code.
    // Suppressing the warning when one was present told the operator the row was
    // configured while it silently produced nothing, which is exactly how a
    // `<Duration>` inside an ORTB `adm` looked fixable when it wasn't.
    if (src.type === 'calculated' && !src.path?.trim()) {
      issues.push({
        kind: 'missing-value',
        message: src.regex
          ? `\`${target}\` has a pattern but no source path — the pattern is matched against the value at the path, so this won't populate anything.`
          : `\`${target}\` has no source path — it won't populate anything.`,
        ref: target,
      });
      continue;
    }

    // Version-inappropriate: the authored path exists only from a later version.
    if (src.type === 'calculated' && src.path) {
      const since = responseSourceSinceVersion(domain, src.path);
      if (since && !appliesTo({ when: { protocolVersion: { min: since } } }, { protocolVersion: version })) {
        const proto = domain === 'vast-ad-response' ? 'VAST' : 'OpenRTB';
        issues.push({
          kind: 'wrong-place',
          message: `\`${target}\` reads \`${src.path}\`, which is ${proto} ${since}+ — a ${proto} ${version} source won't send it.`,
          ref: target,
        });
      }
    }

    /**
     * **Declining the driver's extraction on a field the optimizer selects on** (`X33a`, `#1267`,
     * ratified by Todd 2026-09-10).
     *
     * A **warning**, never a block, and never an error: *block on an invalid value, never on a
     * missing one*, and this is neither — it is a valid configuration whose consequence an operator
     * would not otherwise see. Todd's own answer to *does it interact with importance*: **yes, and
     * for response mapping that means used by optimizer.**
     *
     * The severity is earned by what happens rather than by the field being important. A mapping
     * that misses on a served field costs one wrong value; the same miss on a field the optimizer
     * selects on leaves the ad **unsellable**, and nothing on the serving path says the operator's
     * own decline caused it. That is the *silent and total* case `#1267` was filed about, and it is
     * the only case that survives the ruling — which is why the warning is scoped to the eight
     * rather than to all twenty-four (`#1259`: mark what contradicts the assumption).
     */
    if (entry.acceptSystemDefault === false && usedByOptimizer(canonicalTarget(target)) === true) {
      issues.push({
        kind: 'at-risk',
        message: `\`${target}\` is one of the fields the optimizer selects on, and this row clears the driver's own value when it resolves to nothing. A bid that misses here cannot be sold. Give the row a default if you want your own value instead.`,
        ref: target,
      });
    }
  }

  return issues;
}

/**
 * **The grain marks a store destination carries** (`X32` questions 2 and 3, `#1248`).
 *
 * Two facts, and they render in different places because *one mark per claim, at the level the
 * claim is made* — Q1's collapse rule, applied to the other two questions:
 *
 * | | The claim is made at | So it renders |
 * | --- | --- | --- |
 * | **lifetime** | the **root** — every offered target under `ad` has the same one | once, beside the column that names the root ({@link TARGET_LIFETIME_NOTES}) |
 * | **the optimizer** | the **path** — 8 of the offered targets reach it, the rest do not | per path, in the type-down and on the row |
 *
 * That answers the handoff's open question *does a lifetime belong in a type-down at all*: repeating
 * one identical sentence down twenty-four suggestions teaches nothing and buries the mark that does
 * vary. A path whose lifetime **differs from its root's** is a different claim and is marked here —
 * there are none today, and declaring the exception path now is what makes the first one an addition
 * rather than a redesign.
 */
export interface TargetGrain {
  /** Present only where this path's lifetime differs from the root's — the root's is stated once. */
  lifetime?: { phrase: string; reason: string };
  /**
   * Present only when the answer is *yes* — flipped 2026-09-08 (`#1259`).
   *
   * The mark is still the exception; the assumption it is the exception to is the author's, and
   * Todd's is *what I map is stored on the ad and served*. So *this one also chooses which ads run*
   * is the fact that adds something, and it lands on eight rows rather than sixteen. See
   * {@link USED_BY_OPTIMIZER}.
   */
  usedByOptimizer?: { label: string; detail: string };
}

/** What this surface has to say about a target path. Empty means nothing to say, never *no*. */
export function targetGrain(path: string): TargetGrain {
  const canonical = canonicalTarget(path);
  const grain: TargetGrain = {};

  const root = canonical.split('.')[0];
  const own = targetLifetime(canonical);
  const rootLifetime = targetLifetime(root);
  if (own && rootLifetime && own.lifetime !== rootLifetime.lifetime) {
    grain.lifetime = { phrase: describeLifetime(own.lifetime), reason: own.reason };
  }

  if (usedByOptimizer(canonical) === true) {
    grain.usedByOptimizer = { label: USED_BY_OPTIMIZER, detail: USED_BY_OPTIMIZER_DETAIL };
  }
  return grain;
}

/**
 * The lifetime of each destination namespace, stated once — `X32` Q2 at the level its claim is made.
 *
 * One entry today, and the root is carried rather than assumed so a second namespace (`#1235`'s
 * `pod`) is an entry here rather than a rewrite of the sentence.
 */
export const TARGET_LIFETIME_NOTES: ReadonlyArray<{
  root: string;
  /** The claim as a **scope**, for the column heading — *each ad break*. */
  scope: string;
  /** The same claim as a sentence, kept for the hover and the accessible tree. */
  note: string;
  reason: string;
}> = TARGET_ROOTS.map((r) => ({
  root: r.name,
  scope: describeLifetimeScope(r.lifetime),
  note: describeLifetime(r.lifetime),
  reason: r.lifetimeReason,
}));

/**
 * **What the destination column says about itself** — one line, from the declaration.
 *
 * Todd, 2026-09-10, on the rehearsal: the three-line lifetime sentence under the heading is more
 * than the heading has to spend, and *Mapped for each ad break* carries the scope. The sentence is
 * not deleted with it — it moves to {@link TARGET_LIFETIME_CAVEAT_DETAIL}, which the surface renders
 * where a hover and a screen reader can both reach it.
 */
export const TARGET_LIFETIME_CAVEAT = TARGET_LIFETIME_NOTES.map((n) => `Mapped for ${n.scope}`).join(' · ');

/** The full sentence the heading no longer prints — same claim, for the audience that needs it. */
export const TARGET_LIFETIME_CAVEAT_DETAIL = TARGET_LIFETIME_NOTES.map((n) => n.note).join(' · ');

/** Autocomplete for the store (target) side, hiding already-mapped targets and carrying the marks. */
export function targetSuggestions(query: string, presentTargets: ReadonlyArray<string> = []): MappingSuggestion[] {
  // Canonicalised on both sides: a stored target is whatever spelling it was authored in, and
  // *already mapped* is a question about the field rather than about how it is written (`X49`).
  const present = new Set(presentTargets.map(canonicalTarget));
  const q = query.trim().toLowerCase();
  return AD_TARGET_CATALOG.filter((f) => !present.has(f.name) && (!q || f.name.toLowerCase().includes(q)))
    .map((f) => ({ value: f.name, label: f.name, category: f.importance, description: f.description }))
    .map(withGrainMark);
}

/**
 * **The mark is text, because the type-down is a native `<datalist>`** — an `<option>` renders its
 * value and its text content and nothing else. Q1 hit the same wall and recorded it as a constraint
 * on the design rather than a choice within it; the same holds here, so `label` carries the mark and
 * `value` stays the path that gets inserted.
 *
 * And it carries **only** the mark: the option's own value is already the line above it, so a label
 * repeating the path prints it twice (`markSuggestion`, `#1259`).
 */
function withGrainMark(suggestion: MappingSuggestion): MappingSuggestion {
  const grain = targetGrain(suggestion.value);
  return markSuggestion(suggestion, [grain.lifetime?.phrase, grain.usedByOptimizer?.label]);
}
